import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Modal, notification } from "antd";
import { api } from "../utils/Api";
import { getDisabledDate, getDisabledTime } from "./shiftGroupUtils";

// Changes the assigned date of a shift. Past dates and times are disabled.
const DriverShiftEditModal = ({ open, onClose, onSuccess, shift }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();
	const [modalApi, modalContextHolder] = Modal.useModal();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const onFinish = (values) => {
		// Same date, hour and minute: nothing to update
		if (values.date.isSame(dayjs(shift.rawDate), "minute")) {
			onClose();
			return;
		}

		modalApi.confirm({
			title: "Change shift date",
			content: (
				<>
					<p>
						The shift will move from {dayjs(shift.rawDate).format("MM/DD/YYYY hh:mm A")}{" "}
						to {values.date.format("MM/DD/YYYY hh:mm A")}.
					</p>
					<p>
						The shift start and shift end of every member will move by the same
						difference, and each driver will receive an SMS with the schedule
						change. Continue?
					</p>
				</>
			),
			okText: "Save",
			cancelText: "Cancel",
			centered: true,
			onOk: () => updateDate(values),
		});
	};

	const updateDate = async (values) => {
		const url = api + `/shift/date/${shift.id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.patch(url, {
				date: values.date.toDate(),
				timezoneOffset: values.date.toDate().getTimezoneOffset(),
			});
			if (data.success) {
				form.resetFields();
				// Drivers whose schedule change SMS couldn't be sent
				const failed = data.payload?.sms?.failed || [];
				onSuccess(
					data.message,
					failed.length > 0
						? `Skipped: ${failed
								.map((item) => `${item.driver} (${item.reason})`)
								.join(", ")}`
						: undefined,
				);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to update shift");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Change Shift Date"
			open={open}
			onCancel={onClose}
			centered
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			{modalContextHolder}
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onFinish={onFinish}
				preserve={false}
			>
				<Form.Item
					name="date"
					label="Date"
					initialValue={shift ? dayjs(shift.rawDate) : undefined}
					rules={[
						{ required: true, message: "Date is required" },
						{
							validator: (_, value) =>
								!value ||
								(shift && value.isSame(dayjs(shift.rawDate), "minute")) ||
								!value.isBefore(dayjs(), "minute")
									? Promise.resolve()
									: Promise.reject(
											new Error("Date can't be earlier than the current time"),
									  ),
						},
					]}
				>
					<DatePicker
						size="large"
						showTime={{ format: "hh:mm A" }}
						format="MM/DD/YYYY hh:mm A"
						disabledDate={(current) => getDisabledDate(dayjs())(current)}
						disabledTime={(current) => getDisabledTime(dayjs(), false)(current)}
						allowClear={false}
						style={{ width: "100%" }}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default DriverShiftEditModal;
